import { useEffect, useState } from 'react'
import { Table, Button, Space, Modal, Form, Input, Select, message, Tag, Popconfirm } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { inventoryApi } from '../services/api'
import type { ColumnsType } from 'antd/es/table'

interface Warehouse {
  id: number
  name: string
  type: number
  address: string
  manager: string
  phone: string
  status: number
  remark: string
  created_at: string
}

function Warehouses() {
  const [data, setData] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Warehouse | null>(null)
  const [form] = Form.useForm()

  const fetchData = async () => {
    setLoading(true)
    try {
      const res: any = await inventoryApi.getWarehouses()
      if (res.code === 200) setData(res.data || [])
    } catch { message.error('获取仓库列表失败') }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchData() }, [])

  const handleAdd = () => { setEditing(null); form.resetFields(); setModalOpen(true) }
  const handleEdit = (r: Warehouse) => { setEditing(r); form.setFieldsValue(r); setModalOpen(true) }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      let res: any
      if (editing) {
        res = await inventoryApi.updateWarehouse(editing.id, values)
      } else {
        res = await inventoryApi.createWarehouse(values)
      }
      if (res.code === 200) {
        message.success(editing ? '更新成功' : '创建成功')
        setModalOpen(false)
        fetchData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch { /* validation */ }
  }

  const handleDelete = async (id: number) => {
    try {
      const res: any = await inventoryApi.deleteWarehouse(id)
      if (res.code === 200) { message.success('删除成功'); fetchData() }
      else message.error(res.message)
    } catch { message.error('删除失败') }
  }

  const columns: ColumnsType<Warehouse> = [
    { title: '仓库名称', dataIndex: 'name', key: 'name' },
    { title: '类型', dataIndex: 'type', key: 'type', render: v => v === 1 ? <Tag color="blue">原材料仓</Tag> : <Tag color="purple">成品仓</Tag> },
    { title: '地址', dataIndex: 'address', key: 'address', render: t => t || '-' },
    { title: '管理员', dataIndex: 'manager', key: 'manager', render: t => t || '-' },
    { title: '联系电话', dataIndex: 'phone', key: 'phone', render: t => t || '-' },
    { title: '状态', dataIndex: 'status', key: 'status', render: v => <Tag color={v === 1 ? 'green' : 'red'}>{v === 1 ? '正常' : '停用'}</Tag> },
    { title: '备注', dataIndex: 'remark', key: 'remark', render: t => t || '-' },
    {
      title: '操作',
      key: 'action',
      render: (_, r) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} style={{ backgroundColor: '#55266c' }} onClick={handleAdd}>新建仓库</Button>
      </div>
      <Table columns={columns} dataSource={data} rowKey="id" loading={loading} pagination={false} />
      <Modal title={editing ? '编辑仓库' : '新建仓库'} open={modalOpen} onOk={handleSubmit} onCancel={() => setModalOpen(false)} okText="保存" okButtonProps={{ style: { backgroundColor: '#55266c' } }}>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="仓库名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="仓库名称" />
          </Form.Item>
          <Form.Item name="type" label="仓库类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select placeholder="选择类型">
              <Select.Option value={1}>原材料仓</Select.Option>
              <Select.Option value={2}>成品仓</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input placeholder="仓库地址" />
          </Form.Item>
          <Form.Item name="manager" label="管理员">
            <Input placeholder="管理员姓名" />
          </Form.Item>
          <Form.Item name="phone" label="联系电话">
            <Input placeholder="联系电话" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea placeholder="备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Warehouses
